import { useCallback, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowDown, ArrowUp, Search } from "lucide-react";
import LoadingSpinner from "@/components/LoadingSpinner";
import PrimaryButton from "@/components/PrimaryButton";
import Line from "@/components/Line";
import StudentWelcome from "./StudentWelcome";
import LessonCard from "./LessonCard";
import { AssessmentStatuses } from "./AssessmentsDropdown";
import api from "@/utils/axios-utils";
import { withAsOf } from "@/utils/as-of";
import {
  MOVED_TO_FINISHED_NOTICE,
  movedToFinishedLessonId,
} from "@/utils/movedToFinished";
import {
  NotificationType,
  useNotificationStore,
} from "@/store/useNotificationStore";
import { getDisplayName, useAuth } from "@/hooks/useAuth";
import {
  normalizeScheduleTab,
  paginateScheduleLessons,
  sortScheduleLessons,
  type ScheduleSortKey,
  type ScheduleTab,
} from "./studentLessonScheduleUtils";

type ProgressStatus = "mastered" | "in_progress" | "not_started";

type ScheduleLesson = {
  lesson_id: number;
  name: string;
  description?: string | null;
  created_at: string;
  updated_at: string;
  teacher_id: number;
  status: ProgressStatus;
  action: "learn_again" | "resume" | "start";
  assigned_at?: string | null;
  assigned_for_date?: string | null;
  sequence_position?: number | null;
  assignment_source: "manual" | "auto_sequenced";
  can_start: boolean;
  blocking_reason?: string | null;
  goals_met: number;
  total_goals: number;
  finished_at?: number | null;
  started_at?: number | null;
};

type ScheduleResponse = {
  classroom_id: number;
  classroom_name: string;
  mode: "sequenced" | "legacy";
  incomplete_mode: "pacing" | "completion";
  // "none" is No expectation mode: one lesson at a time at the student's
  // pointer, no dates anywhere, and Upcoming is the rest of the curriculum,
  // locked.
  assignment_mode?: "weekly" | "daily" | "none";
  student_state: "active" | "pending_placement" | "exhausted";
  pending_reason?: string | null;
  summary: {
    total_time_ms: number;
    finished_count: number;
    incomplete_count: number;
    pending_count: number;
  };
  today: ScheduleLesson[];
  incomplete: ScheduleLesson[];
  finished: ScheduleLesson[];
  upcoming: ScheduleLesson[];
  // No expectation mode: the lesson finishing Today leads to. Not always the
  // first Upcoming card -- the walk passes lessons already done this round --
  // and the teacher's roster names the same one as Next (R26).
  up_next_lesson_id?: number | null;
};

// Weekly classrooms hand out the whole week at once, so the leading tab is the
// week rather than the day. The key stays `today` -- it is the same section,
// renamed, and changing it would break a bookmarked ?state=today.
const tabsFor = (
  weekly: boolean,
): Array<{ value: ScheduleTab; label: string }> => [
  { value: "today", label: weekly ? "This week's lessons" : "Today's lesson" },
  { value: "incomplete", label: "Incomplete" },
  { value: "finished", label: "Finished" },
  { value: "upcoming", label: "Upcoming" },
];

// `today` is derived per classroom from `incomplete_mode` (see `todayGuidance`),
// so it is deliberately absent here rather than carrying a string that would be
// wrong for one of the two modes.
const guidance: Record<Exclude<ScheduleTab, "today">, string> = {
  incomplete: "Resume any of these lessons when you want.",
  finished: "These lessons are complete. You can learn them again anytime.",
  // Not the export's wording, which says these unlock once today's lessons are
  // finished. That is not our rule: `student_schedule` puts a lesson in
  // `upcoming` only when `assigned_for_date > today`, so the date is the gate
  // and finishing today's work does nothing for it.
  //
  // Nor does this promise availability *on* that date. When it arrives the
  // lesson is `assigned_for_date == today`, and under
  // `incomplete_mode="completion"` earlier unfinished work can still hold it
  // back. So describe the schedule and let the day itself answer the rest.
  upcoming: "Upcoming lessons are scheduled for later dates.",
};

// Weekly mode replaces two of those. Upcoming becomes next week, and next
// week's contents are not settled: whatever is unfinished this week carries
// over and takes places in it. So the copy says "if you finish this week"
// rather than presenting a list that will change on Monday.
const weeklyGuidance: Record<Exclude<ScheduleTab, "today">, string> = {
  incomplete: "Lessons left over from an earlier week.",
  finished: "These lessons are complete. You can learn them again anytime.",
  upcoming: "Your next lessons, if you finish this week.",
};

// No expectation mode draws no guidance line under the tabs (Alloy, student
// v7). The daily sentences -- "Follow your teacher's pacing settings",
// "scheduled for later dates" -- would mislead here, and the one thing a
// student wonders on Finished, whether practising moves them, is said in the
// tutor when they open one (`fromTab` on the card).
const noExpectationGuidance: Record<ScheduleTab, string | null> = {
  today: null,
  incomplete: null,
  finished: null,
  upcoming: null,
};

// No expectation mode has no dates to sort on, and every list in it is a slice
// of one walk through the curriculum, so curriculum order is offered first and
// is the default (R36, decision 22). Each mode's first option is its default.
const sortOptionsFor = (
  noExpectation: boolean,
): Array<{ value: ScheduleSortKey; label: string }> =>
  noExpectation
    ? [
        { value: "curriculum", label: "Curriculum order" },
        { value: "name", label: "Lesson name" },
      ]
    : [
        { value: "status", label: "Status" },
        { value: "name", label: "Lesson name" },
        { value: "date", label: "Created date" },
      ];

type EmptyState = { title: string; description?: string };

// How long the list keeps asking whether a lesson just finished from
// Incomplete has reached Finished: once a second, five times.
const MOVED_TO_FINISHED_RECHECKS = 5;
const MOVED_TO_FINISHED_RECHECK_MS = 1000;

const END_OF_LESSONS: EmptyState = {
  title: "No more lessons",
  description: "You've reached the end of your lessons. Great work!",
};

/**
 * No expectation mode's empty list for a tab, and the separate one for a search
 * that matched nothing. Every tab keeps its search here, so a student who typed
 * a filter is told it was the filter, not that the tab is empty.
 */
const noExpectationEmptyState = (
  tab: ScheduleTab,
  searching: boolean,
): EmptyState => {
  if (searching) {
    return {
      title:
        tab === "incomplete"
          ? "No incomplete lessons match your search."
          : tab === "finished"
            ? "No finished lessons match your search."
            : tab === "upcoming"
              ? "No upcoming lessons match your search."
              : "No lessons match your search.",
    };
  }
  switch (tab) {
    case "incomplete":
      return { title: "No incomplete lessons." };
    case "finished":
      return { title: "No finished lessons yet." };
    case "upcoming":
      return { title: "No upcoming lessons. You're on the last one." };
    // Today holds the lesson at the pointer, so it is empty only once the
    // pointer has run off the end of the curriculum.
    default:
      return END_OF_LESSONS;
  }
};

// The heading above each list, as the export labels them. Deliberately not
// derived from `tabs`: the tab reads "Incomplete", the section reads
// "Incomplete lessons".
const sectionTitlesFor = (weekly: boolean): Record<ScheduleTab, string> => ({
  today: weekly ? "This week's lessons" : "Today's lesson",
  incomplete: "Incomplete lessons",
  finished: "Finished lessons",
  upcoming: weekly ? "Next week's lessons" : "Upcoming lessons",
});

/**
 * The export's three weekly card states: Available, Started, Pending.
 *
 * "Started" is the middle one -- the lesson has been opened but not finished,
 * so it can be resumed without being the fresh lesson the week is waiting on.
 * Derived from the same two fields the card's own action already reads, so the
 * pill and the button cannot disagree.
 */
const weeklyAvailability = (lesson: ScheduleLesson) => {
  if (lesson.status === "in_progress") {
    return { label: "Started", available: true, tone: "started" as const };
  }
  return lesson.can_start
    ? { label: "Available", available: true, tone: "available" as const }
    : { label: "Pending", available: false, tone: "pending" as const };
};

const toLessonCard = (lesson: ScheduleLesson, { undated = false } = {}) => ({
  id: lesson.lesson_id,
  name: lesson.name,
  description: lesson.description || "",
  created_at: lesson.created_at,
  updated_at: lesson.updated_at,
  teacher_id: lesson.teacher_id,
  status: lesson.status,
  goalsMet: lesson.goals_met,
  totalGoals: lesson.total_goals,
  // An undated card -- every card in No expectation mode -- shows no
  // assignment date. The mode plans none: the row's `assigned_at` is when the
  // pointer row was written, and the authoring date would read as a schedule
  // date too. Started and Finished dates are real and still show.
  undated,
  assignedAt: undated ? undefined : lesson.assigned_at || lesson.created_at,
  scheduledFor: undated ? null : lesson.assigned_for_date,
  finishedAt: lesson.finished_at,
  startedAt: lesson.started_at,
});

const getError = (error: unknown) => {
  if (typeof error === "object" && error && "response" in error) {
    const response = (error as { response?: { data?: { detail?: string } } })
      .response;
    return response?.data?.detail || "Failed to load your lesson schedule.";
  }
  return "Failed to load your lesson schedule.";
};

const ScheduleState = ({ title, description }: EmptyState) => (
  <section className="flex min-h-[360px] flex-col items-center justify-center gap-4 px-4 py-12 text-center">
    <div className="flex h-16 w-16 items-center justify-center rounded-full border border-[#9AA884] bg-[#EEF0E3] text-2xl text-[#28543D]">
      ✓
    </div>
    <h2 className="max-w-xl text-2xl font-extrabold text-[#294B39] md:text-3xl">
      {title}
    </h2>
    {description && (
      <p className="max-w-xl text-base text-[#687368]">{description}</p>
    )}
  </section>
);

const StudentLessonScheduleView = ({
  classroomId,
  legacyFallback,
}: {
  classroomId: string;
  legacyFallback: ReactNode;
}) => {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const showNotification = useNotificationStore(
    (state) => state.showNotification,
  );
  const [schedule, setSchedule] = useState<ScheduleResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState<ScheduleSortKey>("status");
  // Whether the student has actually reached for the sort. The weekly list
  // has to arrive in sequence order -- "all n lessons for the week in
  // sequence order" is the rule, and the default "Status" sort would scramble
  // it on first paint. The control still renders, as the design has it, and
  // sorting works the moment it is used: the gate is `can_start`, which is
  // order-independent, so a reordered view never lets a lesson be taken early.
  const [sortTouched, setSortTouched] = useState(false);
  const [ascending, setAscending] = useState(true);
  const [page, setPage] = useState(1);
  const [loadingLessonId, setLoadingLessonId] = useState<number | null>(null);
  const [assessmentStatusMap, setAssessmentStatusMap] = useState<
    Record<string, AssessmentStatuses>
  >({});

  const activeTab = normalizeScheduleTab(searchParams.get("state"));

  const fetchSchedule = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get<ScheduleResponse>(
        `/classrooms/${classroomId}/lesson-schedule`,
      );
      setSchedule(response.data);
      if (response.data.mode === "sequenced") {
        const allLessons = [
          ...response.data.today,
          ...response.data.incomplete,
          ...response.data.finished,
          ...response.data.upcoming,
        ];
        const lessonIds = [
          ...new Set(allLessons.map((lesson) => lesson.lesson_id)),
        ];
        if (lessonIds.length) {
          try {
            const statusResponse = await api.get<
              Record<string, AssessmentStatuses>
            >("/exit-ticket/assessment-statuses", {
              params: { lesson_ids: lessonIds.join(",") },
            });
            setAssessmentStatusMap(statusResponse.data);
          } catch (statusError) {
            console.error("Error fetching assessment statuses:", statusError);
          }
        }
      }
    } catch (loadError) {
      setError(getError(loadError));
    } finally {
      setLoading(false);
    }
  }, [classroomId]);

  useEffect(() => {
    void fetchSchedule();
  }, [fetchSchedule]);

  useEffect(() => {
    setPage(1);
  }, [activeTab, search, sortBy, ascending]);

  const noExpectation = schedule?.assignment_mode === "none";
  const sortOptions = sortOptionsFor(noExpectation);
  // The state starts at "status" before the mode is known, and No expectation
  // mode does not offer it. A key the mode does not offer reads as the mode's
  // default, so the select never shows a value missing from its own options.
  const effectiveSortBy = sortOptions.some((option) => option.value === sortBy)
    ? sortBy
    : sortOptions[0].value;
  // No expectation mode's Upcoming is the road ahead in the order the student
  // will meet it (Alloy, student v7), so the sort has nothing to do there: it
  // shows curriculum order and is disabled, rather than taking a choice it
  // would ignore.
  const sortFixed = noExpectation && activeTab === "upcoming";
  const sortFixedReason = sortFixed
    ? "Upcoming lessons are always in curriculum order."
    : undefined;

  const visibleLessons = useMemo(() => {
    if (!schedule) return [];
    const query = search.trim().toLowerCase();
    const source = schedule[activeTab];
    const filtered = source.filter(
      (lesson) => !query || lesson.name.toLowerCase().includes(query),
    );
    // Sequence order is the point of the weekly list: only the leading
    // unfinished lesson can be started, and the rest are Pending. Sorting it
    // alphabetically would put a Pending card above the one Start card and make
    // the gate look broken. The server's order is already correct.
    const weekly = (schedule.assignment_mode ?? "weekly") === "weekly";
    if (weekly && activeTab === "today" && !sortTouched) return filtered;
    // No expectation mode's Upcoming is the road ahead, in the order the
    // student will meet it, whatever the sort says (Alloy, student v7).
    if (noExpectation && activeTab === "upcoming") {
      return sortScheduleLessons(filtered, "curriculum", true);
    }
    return sortScheduleLessons(filtered, effectiveSortBy, ascending);
  }, [
    activeTab,
    ascending,
    effectiveSortBy,
    noExpectation,
    schedule,
    search,
    sortTouched,
  ]);

  // Back from finishing a lesson from Incomplete: say it moved to Finished --
  // but only once the schedule shows it there, so the notice is never ahead of
  // what the list says (Alloy, student v7). The tutor stops waiting for the
  // session's last write at 1.5s, so the first read can predate it: the list
  // reads again, quietly, a bounded number of times before giving up. Said at
  // most once: the router state is cleared, so a refresh or Back does not
  // repeat it.
  const movedToFinishedId = movedToFinishedLessonId(location.state);
  const completionContext = `${classroomId}:${location.key}:${movedToFinishedId}`;
  const [completionRechecks, setCompletionRechecks] = useState({
    context: "",
    count: 0,
  });
  const movedToFinishedRechecks =
    completionRechecks.context === completionContext
      ? completionRechecks.count
      : 0;
  useEffect(() => {
    if (
      !schedule ||
      String(schedule.classroom_id) !== classroomId ||
      movedToFinishedId === null
    )
      return undefined;
    const landed = schedule.finished.some(
      (lesson) => lesson.lesson_id === movedToFinishedId,
    );
    if (
      noExpectation &&
      !landed &&
      movedToFinishedRechecks < MOVED_TO_FINISHED_RECHECKS
    ) {
      let cancelled = false;
      const timer = setTimeout(async () => {
        try {
          const response = await api.get<ScheduleResponse>(
            `/classrooms/${classroomId}/lesson-schedule`,
          );
          if (!cancelled) setSchedule(response.data);
        } catch {
          // The next check, or giving up, takes it from here.
        } finally {
          // Count only this context's settled response, not a request still
          // in flight or one superseded by navigation.
          if (!cancelled)
            setCompletionRechecks((current) => ({
              context: completionContext,
              count:
                current.context === completionContext ? current.count + 1 : 1,
            }));
        }
      }, MOVED_TO_FINISHED_RECHECK_MS);
      return () => {
        cancelled = true;
        clearTimeout(timer);
      };
    }
    navigate(`${location.pathname}${location.search}`, {
      replace: true,
      state: null,
    });
    if (noExpectation && landed) {
      showNotification(MOVED_TO_FINISHED_NOTICE, NotificationType.SUCCESS);
    }
    return undefined;
  }, [
    schedule,
    movedToFinishedId,
    movedToFinishedRechecks,
    completionContext,
    noExpectation,
    classroomId,
    location.pathname,
    location.search,
    navigate,
    showNotification,
  ]);

  if (loading) return <LoadingSpinner message="Loading lessons..." />;

  if (error || !schedule) {
    return (
      <div className="flex min-h-[360px] flex-col items-center justify-center gap-4 text-center">
        <p role="alert" className="text-lg text-gray-500">
          {error || "Schedule unavailable."}
        </p>
        <PrimaryButton onClick={() => void fetchSchedule()}>
          Try Again
        </PrimaryButton>
      </div>
    );
  }

  if (schedule.mode === "legacy") return <>{legacyFallback}</>;

  const { pageCount, lessons: pagedLessons } = paginateScheduleLessons(
    visibleLessons,
    page,
    activeTab === "today" ? visibleLessons.length : 3,
  );
  const hasAnyLesson = [
    schedule.today,
    schedule.incomplete,
    schedule.finished,
    schedule.upcoming,
  ].some((lessons) => lessons.length > 0);
  // Asked of every section, not just Today. In weekly mode a blocked card does
  // sit in Today, but in daily mode it cannot: `daily_start_gate` clears
  // `can_start` for every row the day serves, and the row it holds back --
  // dated today but outranked by unfinished earlier work -- is filed under
  // Upcoming. Reading Today alone therefore made this permanently false in
  // daily mode, and the one sentence that explains the completion rule was
  // unreachable in the only mode where that rule needs explaining. The
  // blocking reason itself is the signal, so match on it rather than on which
  // section the card happened to land in.
  const hasCompletionBlockedLesson = [
    schedule.today,
    schedule.upcoming,
    schedule.incomplete,
  ].some((lessons) =>
    lessons.some(
      (lesson) =>
        !lesson.can_start &&
        lesson.blocking_reason === "Complete earlier lessons first",
    ),
  );
  // Three sentences, because the rule genuinely differs and a single hedge was
  // wrong in both directions: it implied incomplete work might block under
  // `pacing` (it never does) and said nothing useful under `completion` (where
  // it does). `incomplete_mode` comes from the schedule response for exactly
  // this -- the client has no other route to a teacher setting.
  // The export's Incomplete guidance ends "They do not block today's lesson."
  // That is true under `pacing` and false under `completion`, where earlier
  // unfinished work is exactly what holds today's lesson back -- so the sentence
  // is appended only where it is true, the same rule as the Today copy.
  const weekly = (schedule.assignment_mode ?? "weekly") === "weekly";
  const tabs = tabsFor(weekly);
  const sectionTitles = sectionTitlesFor(weekly);
  const activeGuidance = weekly ? weeklyGuidance : guidance;
  const incompleteGuidance =
    schedule.incomplete_mode === "pacing"
      ? `${activeGuidance.incomplete} They do not block today's lesson.`
      : activeGuidance.incomplete;
  const todayGuidance = weekly
    ? schedule.incomplete_mode === "pacing"
      ? "Work through this week's lessons in order. Starting a lesson unlocks the next one."
      : // The export ships one weekly sentence, and it states the pacing rule.
        // Under completion, starting is not enough -- the lesson has to be
        // finished -- so the verb changes and the rest of the sentence holds.
        "Work through this week's lessons in order. Finishing a lesson unlocks the next one."
    : schedule.incomplete_mode === "pacing"
      ? "Incomplete lessons do not block today's lesson."
      : hasCompletionBlockedLesson
        ? "Complete earlier lessons first."
        : "Follow your teacher's pacing settings.";
  const tabGuidance = noExpectation
    ? noExpectationGuidance[activeTab]
    : activeTab === "today"
      ? todayGuidance
      : activeTab === "incomplete"
        ? incompleteGuidance
        : activeGuidance[activeTab];

  // Past the last lesson (R10). Only Today and Upcoming empty out; Incomplete
  // and Finished keep their lessons, which is why the other modes' "You
  // finished every lesson!" -- drawn over every tab but Finished -- does not
  // apply here. "exhausted" is the server's word for it, and an active student
  // with nothing at the pointer is in the same place.
  const reachedEnd =
    noExpectation &&
    (schedule.student_state === "exhausted" ||
      (schedule.student_state === "active" && schedule.today.length === 0));
  const noExpectationEmpty = !noExpectation
    ? null
    : // Upcoming says what Today says once the end is reached: "You're on the
      // last one" would be false when there is no current one.
      (activeTab === "today" && reachedEnd) ||
        (activeTab === "upcoming" &&
          reachedEnd &&
          schedule.upcoming.length === 0)
      ? END_OF_LESSONS
      : pagedLessons.length === 0
        ? noExpectationEmptyState(activeTab, search.trim().length > 0)
        : null;
  // The lesson the pointer reaches next, as the server walks to it -- past
  // lessons already done this round, as the roster's Next does -- and never by
  // where it lands on screen: under a name sort, or on a later page, "Up next"
  // would otherwise go to whichever card happened to come first. A server
  // that does not send it gets the first card by curriculum position.
  const upNextLesson = !noExpectation
    ? undefined
    : schedule.up_next_lesson_id === undefined
      ? sortScheduleLessons(schedule.upcoming, "curriculum", true)[0]
      : sortScheduleLessons(schedule.upcoming, "curriculum", true).find(
          (lesson) => lesson.lesson_id === schedule.up_next_lesson_id,
        );

  return (
    <div className="flex flex-col gap-8 font-nunito md:gap-12">
      <StudentWelcome
        name={getDisplayName(user)}
        totalTimeMs={schedule.summary.total_time_ms}
        masteredCount={schedule.summary.finished_count}
        inProgressCount={schedule.summary.incomplete_count}
        notStartedCount={schedule.summary.pending_count}
        // The counts were already the schedule's buckets; only the words were
        // the progress view's. These match the tab names below them.
        // No expectation mode's ahead-of-the-pointer lessons are Upcoming,
        // not waiting on a date (Alloy, student v7).
        summaryLabels={[
          "Finished",
          "Incomplete",
          noExpectation ? "Upcoming" : "Pending",
        ]}
        showSummary
      />

      <section className="flex flex-col gap-4">
        <h1 className="text-2xl font-bold text-[#294B39]">
          Your teacher assigned these lessons
        </h1>
        {/* One row, as the export's LessonControls draws it: the tab group on
            the left, then "Sort by:" with the sort select, the direction
            toggle and the search field pushed to the right. Previously the
            tabs, a guidance line and a separate search/sort row stacked into
            three. */}
        <div className="flex flex-row flex-wrap items-center gap-3 lg:flex-nowrap lg:justify-between">
          {/* `max-w-full` is what makes `overflow-x-auto` do anything: as a
              flex item with `shrink-0` and no cap, this grows to the tabs'
              intrinsic width instead of scrolling, and on a phone that pushes
              the page sideways. */}
          <div className="max-w-full shrink-0 overflow-x-auto">
            <nav
              aria-label="Lesson schedule"
              role="tablist"
              className="flex items-center gap-3 pb-1"
            >
              {tabs.map((item) => (
                <button
                  key={item.value}
                  type="button"
                  role="tab"
                  aria-selected={activeTab === item.value}
                  tabIndex={activeTab === item.value ? 0 : -1}
                  onClick={() =>
                    setSearchParams(withAsOf({ state: item.value }))
                  }
                  className={
                    activeTab === item.value
                      ? "whitespace-nowrap rounded-[6px] border border-[#28543D] bg-[#28543D] px-5 py-2.5 text-xs font-semibold text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#28543D]/30"
                      : "whitespace-nowrap rounded-[6px] border border-[#DED8CC] bg-white px-5 py-2.5 text-xs font-semibold text-[#28543D] hover:bg-[#EEF0E3] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#28543D]/30"
                  }
                >
                  {item.label}
                </button>
              ))}
            </nav>
          </div>
          <div className="min-w-[280px] lg:min-w-0 lg:flex-1">
            <div className="flex w-full min-w-0 flex-row flex-wrap items-center justify-end gap-y-2 lg:flex-nowrap">
              <label
                htmlFor="student-lesson-sort"
                className="mr-2 whitespace-nowrap text-sm text-[#25252599]"
              >
                Sort by:
              </label>
              <div className="flex-1 md:w-[162px] md:flex-none md:shrink-0">
                <select
                  id="student-lesson-sort"
                  value={sortFixed ? "curriculum" : effectiveSortBy}
                  disabled={sortFixed}
                  title={sortFixedReason}
                  onChange={(event) => {
                    setSortTouched(true);
                    setSortBy(event.target.value as ScheduleSortKey);
                  }}
                  className="h-11 w-full rounded-lg border border-[#D6CEBF] bg-white px-3 text-sm font-semibold text-[#294B39] focus:border-[#28543D] focus:outline-none disabled:opacity-60"
                >
                  {sortOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
              <button
                type="button"
                aria-label={ascending ? "Sort descending" : "Sort ascending"}
                title={
                  sortFixedReason ??
                  (ascending ? "Sort descending" : "Sort ascending")
                }
                disabled={sortFixed}
                onClick={() => {
                  setSortTouched(true);
                  setAscending((value) => !value);
                }}
                className="ml-2 flex h-11 w-11 shrink-0 items-center justify-center rounded-lg border border-[#DED8CC] bg-white text-[#28543D] hover:bg-[#F3EDE2] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#28543D]/30 disabled:opacity-60 disabled:hover:bg-white"
              >
                {ascending ? (
                  <ArrowUp size={18} aria-hidden="true" />
                ) : (
                  <ArrowDown size={18} aria-hidden="true" />
                )}
              </button>
              <div className="relative min-w-[220px] shrink grow basis-full md:ml-3 md:basis-auto">
                <Search
                  size={17}
                  className="pointer-events-none absolute left-3 top-3 text-[#687368]"
                  aria-hidden="true"
                />
                <label className="sr-only" htmlFor="student-lesson-search">
                  Search lessons
                </label>
                <input
                  id="student-lesson-search"
                  type="search"
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder="Search lessons"
                  className="h-11 w-full rounded-lg border border-[#D6CEBF] pl-10 pr-3 text-sm font-semibold outline-none focus:border-[#28543D] focus:ring-2 focus:ring-[#28543D]/20"
                />
              </div>
            </div>
          </div>
        </div>
        <Line />
        {/* Guidance sits under the divider in the export, not above the
            controls. */}
        {tabGuidance && (
          <p className="text-sm font-semibold text-[#687368]">{tabGuidance}</p>
        )}
      </section>

      {schedule.student_state === "pending_placement" && !hasAnyLesson ? (
        <ScheduleState
          title="Your teacher is setting up your lessons. Check back soon."
          description={
            schedule.pending_reason ||
            "Your lessons will appear here as soon as they are ready."
          }
        />
      ) : noExpectationEmpty ? (
        <ScheduleState {...noExpectationEmpty} />
      ) : !noExpectation &&
        schedule.student_state === "exhausted" &&
        activeTab !== "finished" ? (
        <ScheduleState
          title="You finished every lesson!"
          description="Amazing work. There are no lessons to show right now."
        />
      ) : pagedLessons.length === 0 ? (
        <ScheduleState
          title={
            activeTab === "upcoming"
              ? "Your next lesson is coming soon."
              : "No lessons to show."
          }
          description={
            activeTab === "upcoming"
              ? "Your teacher will add more lessons to your schedule."
              : "Try another schedule tab or check back soon."
          }
        />
      ) : (
        <section
          aria-live="polite"
          aria-labelledby="student-lesson-section-title"
          className="flex flex-col gap-4"
        >
          <h2
            id="student-lesson-section-title"
            className="text-xl font-bold text-[#294B39]"
          >
            {/* No expectation mode heads each list with its tab's name
                (Alloy, student v7). */}
            {noExpectation
              ? tabs.find((tab) => tab.value === activeTab)?.label
              : sectionTitles[activeTab]}
          </h2>
          {pagedLessons.map((lesson, index) => (
            <LessonCard
              // No expectation cards are undated, so the curriculum position is
              // what tells one lesson's two places in a sequence apart.
              key={`${lesson.assignment_source}-${lesson.lesson_id}-${lesson.assigned_for_date || "none"}${noExpectation ? `-${lesson.sequence_position ?? "none"}` : ""}`}
              lesson={toLessonCard(lesson, {
                undated: noExpectation,
              })}
              classroomId={classroomId}
              canStart={lesson.can_start}
              // "Pending" on every tab, which is the export's only word for a
              // blocked action.
              //
              // This used to read "Coming soon" outside the Upcoming tab, which
              // was wrong twice. In the export, "Coming soon" belongs to
              // `ComingSoonCard` -- a placeholder for when *no* next lesson is
              // scheduled ("Date to be announced / Your next lesson") -- so
              // borrowing it told the student nothing is scheduled while a
              // lesson sat right there, dated today. And the block it labelled
              // is a state the export does not model at all: its own
              // today-lesson card has no disabled branch, because the
              // completion gate comes from our pacing settings rather than from
              // the design.
              //
              // The reason still rides along below as the tooltip and for
              // screen readers, and the tab guidance states it in full.
              // "Pending" is the export's word for an Upcoming card waiting on
              // its date. A blocked Today card is waiting on earlier work --
              // a different reason -- and the export has no word for it,
              // because its Today card is never disabled. Reusing "Pending"
              // told the student the wrong thing about why, the same mistake as
              // the "Coming soon" it replaced. The guidance line above carries
              // the reason in full.
              //
              // No expectation mode's Upcoming is not waiting on a date -- it
              // has none -- but on the pointer reaching it, so it reads
              // "Locked" there too.
              unavailableLabel={
                activeTab === "upcoming" && !noExpectation
                  ? "Pending"
                  : "Locked"
              }
              // The server's reason for a No expectation Upcoming card is the
              // word "Locked" itself; passing it on would read "Locked: Locked"
              // to a screen reader.
              unavailableReason={
                noExpectation && lesson.blocking_reason === "Locked"
                  ? undefined
                  : lesson.blocking_reason || undefined
              }
              // Upcoming only: the export draws this pill on its upcoming
              // cards, and a finished lesson labelled "Available" would be
              // nonsense.
              // The weekly list draws the pill on every card, because the
              // sequence gate is what the student is reading it for. Elsewhere
              // it stays on Upcoming only, as the export has it.
              availability={
                weekly && activeTab === "today"
                  ? weeklyAvailability(lesson)
                  : activeTab === "upcoming" && !noExpectation
                    ? {
                        label: lesson.can_start ? "Available" : "Pending",
                        available: lesson.can_start,
                      }
                    : undefined
              }
              // "Lesson 3 of 5" replaces the date pill on a weekly card: the
              // whole batch shares a date, so position is the useful fact.
              sequenceLabel={
                !noExpectation && weekly && activeTab === "today"
                  ? `Lesson ${index + 1} of ${visibleLessons.length}`
                  : undefined
              }
              // No expectation mode's Upcoming card (Alloy, student v7): "Up
              // next" on the lesson the pointer reaches next and "Upcoming" on
              // the rest, in place of the status, and a padlock where the
              // action would be.
              position={
                noExpectation && activeTab === "upcoming"
                  ? lesson === upNextLesson
                    ? { label: "Up next", next: true }
                    : { label: "Upcoming", next: false }
                  : undefined
              }
              locked={
                noExpectation && activeTab === "upcoming" && !lesson.can_start
              }
              fromTab={
                noExpectation &&
                (activeTab === "incomplete" || activeTab === "finished")
                  ? activeTab
                  : undefined
              }
              globalLoadingLessonId={loadingLessonId}
              onStartStateChange={(loading, lessonId) =>
                setLoadingLessonId(loading ? lessonId : null)
              }
              // An Upcoming card carries no mark of an earlier finish, the
              // best score included: the student sees it again when the lesson
              // comes back to Today (decision 1, R24).
              assessmentStatuses={
                noExpectation && activeTab === "upcoming"
                  ? undefined
                  : assessmentStatusMap[String(lesson.lesson_id)]
              }
            />
          ))}
          {pageCount > 1 && (
            <nav
              aria-label={`${activeTab} lesson pages`}
              className="flex items-center justify-center gap-2 pt-2"
            >
              {Array.from({ length: pageCount }, (_, index) => index + 1).map(
                (pageNumber) => (
                  <button
                    key={pageNumber}
                    type="button"
                    aria-current={pageNumber === page ? "page" : undefined}
                    onClick={() => setPage(pageNumber)}
                    className={
                      pageNumber === page
                        ? "h-9 w-9 rounded-[6px] bg-[#28543D] text-sm font-bold text-white"
                        : "h-9 w-9 rounded-[6px] border border-[#DED8CC] bg-white text-sm font-bold text-[#28543D] hover:bg-[#EEF0E3]"
                    }
                  >
                    {pageNumber}
                  </button>
                ),
              )}
            </nav>
          )}
        </section>
      )}
    </div>
  );
};

export default StudentLessonScheduleView;
